// src/pages/AdminDisputasPage.jsx
// Bloque 7 — Módulo de disputas FIXO
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";

const font = "Montserrat, sans-serif";

function formatARS(n) {
  if (!n || isNaN(n)) return "$0";
  return new Intl.NumberFormat("es-AR", {
    style: "currency", currency: "ARS", maximumFractionDigits: 0,
  }).format(Number(n));
}

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "hace un momento";
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return new Date(dateStr).toLocaleDateString("es-AR");
}

const STATUS_MAP = {
  open:         { label: "Abierta",       color: "#b06a00", bg: "rgba(176,106,0,0.1)" },
  under_review: { label: "En revisión",   color: "#7b5ea7", bg: "rgba(123,94,167,0.1)" },
  resolved:     { label: "Resuelta",      color: "#1b7f4e", bg: "rgba(27,127,78,0.1)" },
};

const RESOLUTION_MAP = {
  refund:   { label: "✅ Reembolso aprobado", color: "#1b7f4e" },
  rejected: { label: "❌ Rechazada",          color: "#c0392b" },
  partial:  { label: "⚖️ Parcial",            color: "#7b5ea7" },
};

// ─── Modal resolución ─────────────────────────────────────────────────────────
function ResolveModal({ dispute, onClose, onSuccess }) {
  const [resolution, setResolution] = useState("refund");
  const [adminNote,  setAdminNote]  = useState("");
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");

  const handleResolve = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post(`/disputes/${dispute.id}/resolve`, { resolution, admin_note: adminNote });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || "Error al resolver el reclamo.");
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{ background: "#fff", borderRadius: 18, padding: "32px 28px", width: "min(480px, 100%)", boxShadow: "0 16px 60px rgba(22,50,68,0.22)" }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ width: 3, height: 13, background: "#E7A432", borderRadius: 2 }} />
            <span style={{ fontFamily: font, fontWeight: 800, fontSize: 16, color: "#163244" }}>
              Resolver reclamo #{dispute.id}
            </span>
          </div>
          <p style={{ fontFamily: font, fontSize: 13, color: "#7a93a4", margin: "6px 0 0" }}>
            Cliente: <strong style={{ color: "#163244" }}>{dispute.client_name}</strong> —
            Monto: <strong style={{ color: "#163244" }}>{formatARS(dispute.transaction_amount)}</strong>
          </p>
        </div>

        {/* Motivo del reclamo */}
        <div style={{ background: "#f5f7fa", borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", marginBottom: 4 }}>Motivo del reclamo</div>
          <div style={{ fontSize: 13, color: "#163244" }}>{dispute.reason}</div>
          {dispute.description && <div style={{ fontSize: 12, color: "#7a93a4", marginTop: 4 }}>{dispute.description}</div>}
        </div>

        {/* Respuesta del prestador */}
        {dispute.provider_response && (
          <div style={{ background: "rgba(123,94,167,0.06)", border: "1px solid rgba(123,94,167,0.15)", borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#7b5ea7", textTransform: "uppercase", marginBottom: 4 }}>Respuesta del prestador</div>
            <div style={{ fontSize: 13, color: "#163244" }}>{dispute.provider_response}</div>
          </div>
        )}

        {/* Resolución */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "#163244", display: "block", marginBottom: 6 }}>
            Resolución
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { value: "refund",   label: "✅ Reembolso" },
              { value: "rejected", label: "❌ Rechazar" },
              { value: "partial",  label: "⚖️ Parcial" },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setResolution(opt.value)}
                style={{
                  flex: 1, padding: "9px 6px", borderRadius: 8, fontFamily: font,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  border: resolution === opt.value ? "2px solid #163244" : "1.5px solid rgba(0,0,0,0.12)",
                  background: resolution === opt.value ? "#163244" : "#fff",
                  color: resolution === opt.value ? "#fff" : "#163244",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Nota admin */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "#163244", display: "block", marginBottom: 6 }}>
            Nota para el cliente y prestador <span style={{ fontWeight: 400, color: "#aab8c2" }}>(opcional)</span>
          </label>
          <textarea
            value={adminNote}
            onChange={e => setAdminNote(e.target.value)}
            rows={3}
            placeholder="Explicá brevemente la decisión..."
            style={{
              width: "100%", fontFamily: font, fontSize: 13, padding: "10px 12px",
              borderRadius: 8, border: "1.5px solid rgba(22,50,68,0.15)",
              color: "#163244", resize: "vertical", outline: "none", boxSizing: "border-box",
            }}
          />
        </div>

        {error && (
          <div style={{ background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#c0392b", fontFamily: font }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 50, border: "2px solid rgba(0,0,0,0.1)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={handleResolve} disabled={loading} style={{ flex: 2, padding: "12px", borderRadius: 50, border: "none", background: loading ? "#dce5ec" : "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            {loading
              ? <><span style={{ width: 13, height: 13, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Procesando...</>
              : "Confirmar resolución"
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminDisputasPage() {
  const navigate = useNavigate();

  const [disputes,      setDisputes]      = useState([]);
  const [counts,        setCounts]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filterStatus,  setFilterStatus]  = useState("");
  const [resolveModal,  setResolveModal]  = useState(null);
  const [toast,         setToast]         = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.get("/disputes", { params });
      setDisputes(res.data.disputes || []);
      setCounts(res.data.counts || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchDisputes(); }, [filterStatus]);

  const handleResolveSuccess = () => {
    setResolveModal(null);
    showToast("Reclamo resuelto correctamente.");
    fetchDisputes();
  };

  // Conteos por estado
  const getCount = (status) => {
    const found = counts.find(c => c.status === status);
    return found ? Number(found.count) : 0;
  };

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "24px 24px 60px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <button onClick={() => navigate("/admin/dashboard")} style={{ background: "none", border: "none", cursor: "pointer", color: "#7a93a4", fontFamily: font, fontSize: 13, fontWeight: 500, padding: 0, display: "flex", alignItems: "center", gap: 5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                  Dashboard
                </button>
                <span style={{ color: "#dce5ec" }}>/</span>
                <span style={{ color: "#163244", fontWeight: 600, fontSize: 13 }}>Reclamos</span>
              </div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#163244" }}>Gestión de reclamos</h1>
            </div>
            <button onClick={fetchDisputes} style={{ padding: "9px 20px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.2)", background: "#fff", color: "#163244", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              ↻ Actualizar
            </button>
          </div>

          {/* Métricas */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
            {[
              { label: "Abiertas",     status: "open",         icon: "🔴", color: "#b06a00" },
              { label: "En revisión",  status: "under_review", icon: "🟡", color: "#7b5ea7" },
              { label: "Resueltas",    status: "resolved",     icon: "🟢", color: "#1b7f4e" },
            ].map(({ label, status, icon, color }) => (
              <div key={status} style={{ background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", padding: "16px 18px", cursor: "pointer" }}
                onClick={() => setFilterStatus(filterStatus === status ? "" : status)}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</span>
                  <span style={{ fontSize: 18 }}>{icon}</span>
                </div>
                <div style={{ fontSize: 32, fontWeight: 900, color, lineHeight: 1 }}>{getCount(status)}</div>
                {filterStatus === status && <div style={{ fontSize: 10, color, fontWeight: 700, marginTop: 4 }}>● Filtrando</div>}
              </div>
            ))}
          </div>

          {/* Tabla */}
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden" }}>

            {/* Header tabla */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 3, height: 14, background: "#E7A432", borderRadius: 2 }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: "#163244" }}>⚖️ Reclamos</span>
                <span style={{ fontSize: 11, color: "#7a93a4" }}>— {disputes.length} registros</span>
              </div>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                style={{ padding: "7px 14px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.15)", fontFamily: font, fontSize: 11, fontWeight: 600, color: "#163244", background: "#f5f7fa", outline: "none", cursor: "pointer" }}
              >
                <option value="">Todos los estados</option>
                <option value="open">Abiertas</option>
                <option value="under_review">En revisión</option>
                <option value="resolved">Resueltas</option>
              </select>
            </div>

            {/* Loading */}
            {loading && (
              <div style={{ padding: "32px", textAlign: "center", color: "#7a93a4", fontSize: 13 }}>
                <span style={{ display: "inline-block", width: 20, height: 20, border: "2px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginRight: 8, verticalAlign: "middle" }} />
                Cargando...
              </div>
            )}

            {/* Sin datos */}
            {!loading && disputes.length === 0 && (
              <div style={{ padding: "48px 20px", textAlign: "center", color: "#aab8c2" }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>⚖️</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#163244", marginBottom: 6 }}>Sin reclamos</div>
                <div style={{ fontSize: 13 }}>Los reclamos aparecerán aquí cuando los clientes las abran.</div>
              </div>
            )}

            {/* Filas */}
            {!loading && disputes.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f8fafb" }}>
                      {["ID", "Cliente", "Prestador", "Monto", "Motivo", "Estado", "Resolución", "Fecha", "Acción"].map(h => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#7a93a4", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid rgba(0,0,0,0.06)", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {disputes.map((d, i) => {
                      const st  = STATUS_MAP[d.status]     || STATUS_MAP.open;
                      const res = RESOLUTION_MAP[d.resolution];
                      return (
                        <tr key={d.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                          <td style={{ padding: "11px 14px", color: "#163244", fontWeight: 700 }}>#{d.id}</td>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ fontWeight: 600, color: "#163244" }}>{d.client_name}</div>
                            <div style={{ fontSize: 10, color: "#aab8c2" }}>{d.client_email}</div>
                          </td>
                          <td style={{ padding: "11px 14px", fontWeight: 600, color: "#163244" }}>{d.provider_name}</td>
                          <td style={{ padding: "11px 14px", fontWeight: 800, color: "#163244" }}>{formatARS(d.transaction_amount)}</td>
                          <td style={{ padding: "11px 14px", maxWidth: 180 }}>
                            <div style={{ color: "#163244", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.reason}</div>
                            {d.description && <div style={{ fontSize: 10, color: "#aab8c2", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.description}</div>}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: st.color, background: st.bg, padding: "3px 10px", borderRadius: 10 }}>
                              {st.label}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            {res
                              ? <span style={{ fontSize: 11, fontWeight: 700, color: res.color }}>{res.label}</span>
                              : <span style={{ fontSize: 11, color: "#aab8c2" }}>—</span>
                            }
                          </td>
                          <td style={{ padding: "11px 14px", color: "#7a93a4", fontSize: 11, whiteSpace: "nowrap" }}>
                            {timeAgo(d.created_at)}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            {d.status !== "resolved" && (
                              <button
                                onClick={() => setResolveModal(d)}
                                style={{ padding: "6px 14px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}
                              >
                                Resolver →
                              </button>
                            )}
                            {d.status === "resolved" && (
                              <span style={{ fontSize: 10, color: "#1b7f4e", fontWeight: 700 }}>✓ Resuelta</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Modal resolución */}
      {resolveModal && (
        <ResolveModal
          dispute={resolveModal}
          onClose={() => setResolveModal(null)}
          onSuccess={handleResolveSuccess}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#163244", color: "#fff", padding: "13px 26px", borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", borderLeft: "4px solid #E7A432", whiteSpace: "nowrap", fontFamily: font }}>
          {toast}
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
